<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { COUNTRIES, parsePhone, composePhone, findByCode } from '@/composables/useCountryCodes.js'
import { useBreakpoint } from '@/composables/useBreakpoint'
import Icon from '@/Components/UI/Icon.vue'

const { t } = useI18n()

const props = defineProps({
  modelValue:  { type: String, default: '' },
  placeholder: { type: String, default: '' },
  required:    { type: Boolean, default: false },
  disabled:    { type: Boolean, default: false },
  error:       { type: String, default: '' },
  id:          { type: String, default: '' },
})
const emit = defineEmits([ 'update:modelValue' ])

const { isMobile } = useBreakpoint()

const initial    = parsePhone(props.modelValue)
const countryCode = ref(initial.country.code)
const national    = ref(initial.national)

const country = computed(() => findByCode(countryCode.value))

// Track whether the next modelValue change comes from our own emit. When the
// user picks (say) US from the dropdown we emit "+1 …", and parsing "+1" back
// picks whichever +1-country resolves first — that would silently revert the
// user's choice for shared dial codes (US/CA, RU/KZ). Suppress the re-parse on
// self-emit; only adopt external rewrites of modelValue.
let suppressParse = false

const emitComposed = () => {
  suppressParse = true
  emit('update:modelValue', composePhone(country.value, national.value))
}

watch(countryCode, emitComposed)
watch(national, emitComposed)

watch(() => props.modelValue, (v) => {
  if (suppressParse) { suppressParse = false; return }
  const parsed = parsePhone(v)
  if (parsed.country.code !== countryCode.value) countryCode.value = parsed.country.code
  if (parsed.national     !== national.value)    national.value    = parsed.national
})

// ── Country picker dropdown ──────────────────────────────────────────────
const open = ref(false)
const query = ref('')
const root = ref(null)
const trigger = ref(null)
const searchInput = ref(null)
const activeIndex = ref(0)

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return COUNTRIES
  // Match on country name or dial code (with/without the leading +).
  const qDial = q.replace(/^\+/, '')
  return COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(q) ||
    c.dial.replace('+', '').startsWith(qDial),
  )
})

function openMenu() {
  if (props.disabled) return
  open.value = true
  query.value = ''
  activeIndex.value = Math.max(0, filtered.value.findIndex(c => c.code === countryCode.value))
  nextTick(() => searchInput.value?.focus())
}
function closeMenu() {
  open.value = false
  nextTick(() => trigger.value?.focus?.())
}
function toggleMenu() { open.value ? closeMenu() : openMenu() }

function pick(c) {
  countryCode.value = c.code
  open.value = false
  nextTick(() => trigger.value?.focus?.())
}

// Keep the highlighted row valid as the filter narrows.
watch(filtered, () => { activeIndex.value = 0 })

function onSearchKey(e) {
  if (e.key === 'ArrowDown') { e.preventDefault(); activeIndex.value = Math.min(activeIndex.value + 1, filtered.value.length - 1); scrollActive() }
  else if (e.key === 'ArrowUp') { e.preventDefault(); activeIndex.value = Math.max(activeIndex.value - 1, 0); scrollActive() }
  else if (e.key === 'Enter') { e.preventDefault(); const c = filtered.value[activeIndex.value]; if (c) pick(c) }
  else if (e.key === 'Escape') { e.preventDefault(); closeMenu() }
}

const listRef = ref(null)
function scrollActive() {
  nextTick(() => {
    const el = listRef.value?.querySelector(`[data-idx="${activeIndex.value}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function onDocClick(e) {
  if (isMobile.value) return // backdrop handles outside-click on mobile
  if (root.value && !root.value.contains(e.target)) open.value = false
}
function onDocKey(e) {
  if (open.value && e.key === 'Escape') closeMenu()
}
onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onDocKey)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onDocKey)
})
</script>

<template>
  <div
    ref="root"
    class="relative"
  >
    <div class="flex items-stretch gap-2">
      <!-- Country trigger -->
      <button
        ref="trigger"
        type="button"
        :disabled="disabled"
        :aria-label="`Country code: ${country.name} ${country.dial}`"
        aria-haspopup="listbox"
        :aria-expanded="open"
        class="flex items-center gap-1.5 pl-2.5 pr-2 rounded-[var(--radius-input)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] text-sm shrink-0 focus:outline-2 focus:outline-[color:var(--accent)] disabled:opacity-50 disabled:cursor-not-allowed"
        @click="toggleMenu"
      >
        <span
          :class="`fi fi-${country.code.toLowerCase()}`"
          class="fi-flag"
          aria-hidden="true"
        />
        <span class="tabular-nums">{{ country.dial }}</span>
        <Icon
          name="chev-d"
          :size="14"
          class="text-[color:var(--muted)]"
        />
      </button>

      <input
        :id="id"
        v-model="national"
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :aria-invalid="error ? 'true' : undefined"
        class="flex-1 w-full px-3 py-2 rounded-[var(--radius-input)] border bg-[color:var(--surface)] text-[color:var(--ink)] text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
        :class="error ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
      >
    </div>
    <p
      v-if="error"
      class="mt-1 text-xs text-[color:var(--danger)]"
    >
      {{ error }}
    </p>

    <!-- Dropdown / bottom-sheet -->
    <Teleport
      to="body"
      :disabled="!isMobile"
    >
      <template v-if="open">
        <!-- Mobile backdrop -->
        <div
          v-if="isMobile"
          class="fixed inset-0 z-40 bg-black/40"
          @click="open = false"
        />

        <div
          :class="[
            'z-50 bg-[color:var(--surface)] border border-[color:var(--line)] shadow-[var(--shadow-lg)] flex flex-col overflow-hidden',
            isMobile
              ? 'fixed inset-x-0 bottom-0 rounded-t-[var(--radius-lg)] max-h-[70vh] pb-[max(env(safe-area-inset-bottom),0.5rem)]'
              : 'absolute left-0 top-full mt-1 w-72 rounded-[var(--radius-input)] max-h-80',
          ]"
          role="listbox"
        >
          <!-- Search -->
          <div class="p-2 border-b border-[color:var(--line)] shrink-0">
            <div class="flex items-center gap-2 px-2 py-1.5 rounded-[var(--radius-input)] border border-[color:var(--line)] bg-[color:var(--bg)]">
              <Icon
                name="search"
                :size="15"
                class="text-[color:var(--muted)] shrink-0"
              />
              <input
                ref="searchInput"
                v-model="query"
                type="text"
                :placeholder="t('common.searchCountry')"
                class="flex-1 bg-transparent text-sm text-[color:var(--ink)] focus:outline-none"
                @keydown="onSearchKey"
              >
              <button
                v-if="query"
                type="button"
                :aria-label="t('common.clear')"
                class="text-[color:var(--muted)] hover:text-[color:var(--ink)] shrink-0"
                @click="query = ''"
              >
                <Icon
                  name="x"
                  :size="14"
                />
              </button>
            </div>
          </div>

          <!-- Options -->
          <div
            ref="listRef"
            class="flex-1 overflow-y-auto py-1"
          >
            <button
              v-for="(c, i) in filtered"
              :key="c.code"
              type="button"
              :data-idx="i"
              role="option"
              :aria-selected="c.code === countryCode"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm"
              :class="[
                i === activeIndex ? 'bg-[color:var(--accentSoft)]' : 'hover:bg-[color:var(--surfaceAlt)]',
                c.code === countryCode ? 'text-[color:var(--accent)]' : 'text-[color:var(--ink)]',
              ]"
              @mouseenter="activeIndex = i"
              @click="pick(c)"
            >
              <span
                :class="`fi fi-${c.code.toLowerCase()}`"
                class="fi-flag shrink-0"
                aria-hidden="true"
              />
              <span class="flex-1 truncate">{{ c.name }}</span>
              <span class="tabular-nums text-[color:var(--muted)]">{{ c.dial }}</span>
              <Icon
                v-if="c.code === countryCode"
                name="check"
                :size="15"
                class="text-[color:var(--accent)] shrink-0"
              />
            </button>
            <p
              v-if="!filtered.length"
              class="px-3 py-6 text-center text-sm text-[color:var(--muted)]"
            >
              {{ t('common.noResults') }}
            </p>
          </div>
        </div>
      </template>
    </Teleport>
  </div>
</template>

<style scoped>
/* Constrain the flag to a tidy 4:3 chip and round its corners. flag-icons
   draws the flag as a background image on the .fi element. */
.fi-flag {
  width: 1.25rem;
  height: 0.9375rem;
  border-radius: 2px;
  background-size: cover;
  box-shadow: inset 0 0 0 1px oklch(0 0 0 / 0.08);
}
</style>
